import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import RunIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { TableStatus } from '../../components/table/TableStatus';
import { usePermission } from '../../hooks/use-permission';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import {
  actions as eoyActions,
  selectors as eoySelectors,
} from '../../store/dux/eoy-report-dux';
import { Section } from '../Section';
import { Spinner } from '../../components/Spinner';

export default function EoyReport() {
  const dispatch = useDispatch();
  const [skipQc, setSkipQc] = useState(false);
  const growerId = useSelector(growerSelectors.selectedId);
  const reports = useSelector(eoySelectors.reports);
  const isAdmin = usePermission('ADMIN_ENTERPRISE_REPORT');

  useEffect(() => {
    if (growerId) dispatch(eoyActions.loadReports(growerId));
  }, [dispatch, growerId]);

  return (
    <Section>
      <Box
        sx={{
          px: 3,
          py: 4,
          '& ul': {
            margin: 0,
            padding: 0,
          },

          '& li': {
            display: 'inline-block',
            listStyle: 'none',
            margin: '6px 8px 6px 0',
            paddingRight: '8px',
            borderRight: '1px solid #e0e0e0',

            '&:last-child': {
              border: 'none',
            },
          },
        }}
      >
        <h1>Enterprise Report</h1>
        {buildReports()}
      </Box>
    </Section>
  );

  function buildReports() {
    if (!reports) return buildLoadingState();
    if (!reports.length) return buildEmptyState();
    return buildTable();
  }

  function buildLoadingState() {
    return <Spinner sx={{ height: 'calc(100vh - 200px)' }} />;
  }

  function buildEmptyState() {
    return <h3>No reports are available at this time</h3>;
  }

  function buildOptions() {
    if (!isAdmin) return null;

    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1">Run Options</Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                value={skipQc}
                onChange={(e) => setSkipQc(e.target.checked)}
                data-testid="skip-qc"
              />
            }
            label="Skip QC checks"
          />
        </FormGroup>
      </Box>
    );
  }

  function buildTable() {
    return (
      <>
        {buildOptions()}
        <Table stickyHeader={false}>
          <TableHead>
            <TableRow>
              <TableCell align="left">Year</TableCell>
              <TableCell align="left">Status</TableCell>
              <TableCell align="left">Reports</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>{reports.map(buildTableRow)}</TableBody>
        </Table>
      </>
    );
  }

  function buildTableRow(reportForYear, key) {
    return (
      <TableRow key={key}>
        <TableCell align="left">{reportForYear.year}</TableCell>
        <TableCell align="left">
          <TableStatus status={reportForYear.status} />
        </TableCell>
        <TableCell align="left">{buildReportLinks(reportForYear)}</TableCell>
        <TableCell align="right">{buildRunButton(reportForYear)}</TableCell>
      </TableRow>
    );
  }

  function buildReportLinks({ links }) {
    return links && <ul>{links.map(buildLink)}</ul>;
  }

  function buildLink({ link, name }, i) {
    return (
      <li key={i}>
        <a target="_blank" href={link} rel="noreferrer">
          {name}
        </a>
      </li>
    );
  }

  function buildRunButton({ year, isRunning, isReadOnly }) {
    return (
      <IconButton
        size="small"
        title={`Generate report for ${year}`}
        disabled={isRunning || isReadOnly}
        onClick={() =>
          dispatch(eoyActions.generateReport(growerId, year, skipQc))
        }
      >
        <RunIcon />
      </IconButton>
    );
  }
}
